import React, { useState, useEffect } from 'react';

import Representative from '~/components/ProjectSections/Representative';
import Client from '~/components/ProjectSections/Client';
import ClientPatterns from '~/components/ProjectSections/ClientPatterns';
import InvoiceClient from '~/components/ProjectSections/InvoiceClient';
import Main from '~/components/ProjectSections/Main';
import ClientContact from '~/components/ProjectSections/ClientContact';
import Records from '~/components/ProjectSections/Records';
import Files from '~/components/ProjectSections/Files';
import RequestFiles from '~/components/ProjectSections/RequestFiles';
import EventInfo from '~/components/ProjectSections/EventInfo';
import Gifts from '~/components/ProjectSections/Gifts';
import EventCosts from '~/components/ProjectSections/EventCosts';
import MaterialDeliveryStatus from '~/components/ProjectSections/MaterialDeliveryStatus';
import CompletionFiles from '~/components/ProjectSections/CompletionFiles';

import api from '~/services/api';

interface IEventProps {
  project_id: string;
  all?: boolean;
}

interface IProject {
  id: string;
  type: string;
  code: string;
  total_event_costs_price: number;
  erp_client_code: string;
  erp_rep_code: string;
  note: string;
  client_contact_name: string;
  client_contact_phone: string;
  client_contact_email: string;
  estimated_event_participants_number: number;
  event_date: Date;
  price_req_by_client?: number;
  generates_sponsorship: boolean;
  material_delivery_status: string;
  erp_invoice_client_code: string;
  project_procedure_id: string;
  event: {
    code: string;
    name: string;
  };
  project_procedure: {
    id: string;
    sequence: number;
  };
}

const Event: React.FC<IEventProps> = ({ project_id, all }) => {
  const [project, setProject] = useState<IProject>();

  useEffect(() => {
    api.get(`/projects/id?id=${project_id}`).then(response => {
      setProject(response.data);
    });
  }, [project_id]);

  function handleRepresentative(): React.ReactNode | null {
    if (project) {
      return (
        <Representative
          erp_rep_code={project.erp_rep_code ? project.erp_rep_code : ''}
        />
      );
    }

    return null;
  }

  function handleClient(): React.ReactNode | null {
    if (project) {
      return (
        <Client
          rep_as_client={false}
          erp_client_code={
            project.erp_client_code ? project.erp_client_code : ''
          }
          erp_rep_code=""
        />
      );
    }

    return null;
  }

  function handleClientPatterns(): React.ReactNode | null {
    if (project) {
      return (
        <ClientPatterns
          erp_client_code={
            project.erp_client_code ? project.erp_client_code : ''
          }
        />
      );
    }

    return null;
  }

  function handleInvoiceClient(): React.ReactNode | null {
    if (
      project &&
      project.erp_invoice_client_code &&
      project.erp_invoice_client_code !== ''
    ) {
      return (
        <InvoiceClient
          erp_invoice_client_code={project.erp_invoice_client_code}
        />
      );
    }

    return null;
  }

  function handleMain(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Main
            code={project.code ? project.code : ''}
            item={
              project.event.code && project.event.name
                ? `(${project.event.code}) - ${project.event.name}`
                : ''
            }
            note={project.note ? project.note : ''}
          />
        );
      }
    }

    return null;
  }

  function handleClientContact(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <ClientContact
            client_contact_name={
              project.client_contact_name ? project.client_contact_name : ''
            }
            client_contact_phone={
              project.client_contact_phone ? project.client_contact_phone : ''
            }
            client_contact_email={
              project.client_contact_email ? project.client_contact_email : ''
            }
          />
        );
      }
    }

    return null;
  }

  function handleRecords(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return <Records project_id={project.id} />;
      }
    }

    return null;
  }

  function handleFiles(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <Files
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  function handleRequestFiles(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <RequestFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  function handleEventInfo(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 1 || all) {
        return (
          <EventInfo
            event_date={project.event_date}
            estimated_event_participants_number={
              project.estimated_event_participants_number
            }
            total_event_costs_price={project.total_event_costs_price}
            price_req_by_client={project.price_req_by_client}
            generates_sponsorship={project.generates_sponsorship}
          />
        );
      }
    }

    return null;
  }

  function handleGifts(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 2 || all) {
        return <Gifts project_id={project.id} />;
      }
    }

    return null;
  }

  function handleEventCosts(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 2 || all) {
        return <EventCosts project_id={project.id} />;
      }
    }

    return null;
  }

  function handleMaterialDeliveryStatus(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 4 || all) {
        return (
          <MaterialDeliveryStatus
            material_delivery_status={project.material_delivery_status}
          />
        );
      }
    }

    return null;
  }

  function handleCompletionFiles(): React.ReactNode | null {
    if (project) {
      if (project.project_procedure.sequence > 6 || all) {
        return (
          <CompletionFiles
            project_id={project.id}
            project_procedure_id={project.project_procedure.id}
          />
        );
      }
    }

    return null;
  }

  return (
    <>
      {handleRepresentative()}
      {handleClient()}
      {handleClientPatterns()}
      {handleInvoiceClient()}
      {handleMain()}
      {handleClientContact()}
      {handleRecords()}
      {handleFiles()}
      {handleRequestFiles()}
      {handleEventInfo()}
      {handleGifts()}
      {handleEventCosts()}
      {handleMaterialDeliveryStatus()}
      {handleCompletionFiles()}
    </>
  );
};

export default Event;
